import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Package,
  UserSearch,
  Search,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import { API } from "@/App";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";

export default function TrainerSellPackageModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [trainerId, setTrainerId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [trainerInfo, setTrainerInfo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [priceList, setPriceList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSellModal, setShowSellModal] = useState(false);
  
  // Member search
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Sale form
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [paidAmount, setPaidAmount] = useState("0");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (user?.user_id) {
      fetchTrainerInfo();
    }
  }, [user]);

  const fetchTrainerInfo = async () => {
    try {
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        if (trainers.length > 0) {
          const trainer = trainers[0];
          setTrainerId(trainer.trainer_id);
          setBusinessId(trainer.business_id);
          setTrainerInfo(trainer);
          
          // Fetch packages and price list
          await fetchPackagesAndPrices(trainer.business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching trainer info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackagesAndPrices = async (bizId) => {
    try {
      const [packagesRes, priceListRes] = await Promise.all([
        fetch(`${API}/businesses/${bizId}/packages`, { credentials: "include" }),
        fetch(`${API}/businesses/${bizId}/price-lists/active`, { credentials: "include" }),
      ]);

      if (packagesRes.ok) {
        setPackages(await packagesRes.json());
      }
      if (priceListRes.ok) {
        setPriceList(await priceListRes.json());
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  // Member search
  const searchMembers = useCallback(async (query) => {
    if (query.length < 2 || !businessId) {
      setMemberSearchResults([]);
      return;
    }
    
    setMemberSearchLoading(true);
    try {
      const response = await fetch(
        `${API}/businesses/${businessId}/members?search=${encodeURIComponent(query)}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setMemberSearchResults(data);
        setShowMemberDropdown(true);
      }
    } catch (error) {
      console.error("Error searching members:", error);
    } finally {
      setMemberSearchLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (memberSearchQuery) {
        searchMembers(memberSearchQuery);
      } else {
        setMemberSearchResults([]);
        setShowMemberDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearchQuery, searchMembers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowMemberDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setMemberSearchQuery(`${member.name || ""} ${member.surname || ""}`);
    setShowMemberDropdown(false);
  };

  // Get price from active price list
  const getPackagePrice = (packageId) => {
    if (!priceList?.prices) return { normal: 0, discount: null };
    const priceInfo = priceList.prices.find(p => p.package_id === packageId);
    return {
      normal: priceInfo?.price || 0,
      discount: priceInfo?.discount_price || null,
    };
  };

  const handlePackageSelect = (pkgId) => {
    setSelectedPackageId(pkgId);
    const prices = getPackagePrice(pkgId);
    // Use discount price if available, otherwise normal price
    setSalePrice((prices.discount || prices.normal).toString());
  };

  const handleSellPackage = async () => {
    if (!selectedMember || !selectedPackageId || !salePrice) {
      toast.error(t("packages:toast_uyePaketVeFiyatSecmelisiniz"));
      return;
    }

    try {
      const response = await fetch(`${API}/member-packages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_id: selectedMember.member_id,
          package_id: selectedPackageId,
          trainer_id: trainerId,
          price: parseTurkishNumber(salePrice),
          paid_amount: parseTurkishNumber(paidAmount) || 0,
          notes: notes || null,
        }),
      });

      if (response.ok) {
        toast.success(t("packages:toast_paketBasariylaSatildi"));
        setShowSellModal(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("packages:packageSellFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const resetForm = () => {
    setSelectedMember(null);
    setMemberSearchQuery("");
    setSelectedPackageId("");
    setSalePrice("");
    setPaidAmount("0");
    setNotes("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  if (!trainerInfo) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <Package className="w-12 h-12 text-grey mx-auto mb-3" />
          <p className="text-grey">{t("common:trainerInfoNotFound")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t('members:packageSale')}</h2>
          <p className="text-grey text-sm">{t("packages:ogrencilerinizePaketSatin")}</p>
        </div>

        <Button
          className="bg-navy hover:bg-navy/90 rounded-xl"
          onClick={() => setShowSellModal(true)}
          data-testid="sell-package-btn"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Paket Sat
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-navy text-white">
                {trainerInfo.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-navy">
                {trainerInfo.name} {trainerInfo.surname}
              </p>
              <p className="text-sm text-grey">
                Branşlar: {trainerInfo.branches?.map(b => getLocalizedBranch(b)).join(", ") || "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Packages */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium">{t("packages:satilabilirPaketler")}</CardTitle>
        </CardHeader>
        <CardContent>
          {packages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => {
                const prices = getPackagePrice(pkg.package_id);
                return (
                  <div
                    key={pkg.package_id}
                    className="p-4 border rounded-xl hover:border-navy transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-navy">{pkg.name}</h4>
                      <Badge variant="secondary">{pkg.session_count} seans</Badge>
                    </div>
                    <div className="text-sm text-grey mb-2">
                      {pkg.validity_days} gün geçerli
                    </div>
                    <div className="flex items-center gap-2">
                      {prices.discount ? (
                        <>
                          <span className="text-grey line-through">{formatPrice(prices.normal)}</span>
                          <span className="font-bold text-green-600">{formatPrice(prices.discount)}</span>
                        </>
                      ) : (
                        <span className="font-bold text-navy">{formatPrice(prices.normal)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-grey py-8">{t("packages:henuzPaketTanimlanmamis")}</p>
          )}
        </CardContent>
      </Card>

      {/* Sell Package Modal */}
      <Dialog open={showSellModal} onOpenChange={setShowSellModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('members:packageSale')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Member Search */}
            <div className="relative">
              <Label className="flex items-center gap-2">
                <UserSearch className="w-4 h-4" />
                Üye Ara
              </Label>
              <div className="relative mt-1">
                <Input
                  ref={searchInputRef}
                  placeholder="Ad, soyad ile ara..."
                  value={memberSearchQuery}
                  onChange={(e) => {
                    setMemberSearchQuery(e.target.value);
                    setSelectedMember(null);
                  }}
                  onFocus={() => memberSearchResults.length > 0 && setShowMemberDropdown(true)}
                  className="rounded-xl pr-10"
                  data-testid="member-search-input"
                />
                {memberSearchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-grey" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {showMemberDropdown && memberSearchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto"
                >
                  {memberSearchResults.map((member) => (
                    <div
                      key={member.member_id}
                      className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleSelectMember(member)}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-navy text-white text-xs">
                          {member.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {member.name} {member.surname}
                        </p>
                        <p className="text-xs text-grey">#{member.member_no}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedMember && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Seçilen: {selectedMember.name} {selectedMember.surname}
                </p>
              )}
            </div>

            {/* Package Selection */}
            <div>
              <Label>Paket *</Label>
              <Select value={selectedPackageId} onValueChange={handlePackageSelect}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("packages:ph_paketSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {packages.map((pkg) => {
                    const prices = getPackagePrice(pkg.package_id);
                    return (
                      <SelectItem key={pkg.package_id} value={pkg.package_id}>
                        {pkg.name} - {pkg.session_count} seans - {formatPrice((prices.discount || prices.normal))}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Price */}
            <div>
              <Label>Satış Fiyatı *</Label>
              <CurrencyInput
                value={salePrice}
                onChange={(val) => setSalePrice(val)}
                className="rounded-xl"
                data-testid="sale-price-input"
              />
            </div>

            {/* Paid Amount */}
            <div>
              <Label>{t("common:odenenTutar")}</Label>
              <CurrencyInput
                value={paidAmount}
                onChange={(val) => setPaidAmount(val)}
                className="rounded-xl"
                data-testid="paid-amount-input"
              />
            </div>

            {/* Notes */}
            <div>
              <Label>Notlar</Label>
              <Textarea
                placeholder={t("common:ph_satisIleIlgiliNotlar")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowSellModal(false);
                  resetForm();
                }}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleSellPackage}
                data-testid="confirm-sell-btn"
              >
                Satışı Tamamla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
